import React, { useState } from 'react';
import axios from 'axios';
import { Search, Clock, User, Building2, AlertCircle, FileText, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrackRequest = () => {
  const [searchData, setSearchData] = useState({
    ticket_id: '',
    email_or_phone: '',
  });
  const [ticket, setTicket] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setSearchData({ ...searchData, [e.target.name]: e.target.value });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsSearching(true);
    setError(null);
    setTicket(null);

    try {
      const response = await axios.post(`${API}/track-ticket.php`, searchData);

      if (response.data?.success) {
        setTicket(response.data.ticket);
      } else {
        setError(response.data?.message || 'Ticket not found.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Unable to find ticket. Please check your details.';
      setError(msg);
    } finally {
      setIsSearching(false);
    }
  };

  // Status badge styles
  const statusStyles = {
    Open: 'bg-blue-100 text-blue-800 border-blue-300',
    Assigned: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    'In Progress': 'bg-orange-100 text-orange-800 border-orange-300',
    Resolved: 'bg-green-100 text-green-800 border-green-300',
    Closed: 'bg-gray-200 text-gray-800 border-gray-300',
  };

  const priorityStyles = {
    Low: 'text-gray-600',
    Medium: 'text-blue-600',
    High: 'text-orange-600',
    Critical: 'text-red-600 font-bold',
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d.replace(' ', 'T')).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-r from-red-600 to-red-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Track Your Request</h1>
          <p className="text-xl text-red-100">
            Enter your Ticket ID along with your registered email or phone to check status.
          </p>
        </div>
      </section>

      {/* Search Form */}
      <section className="py-16 bg-gray-100 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-2xl font-bold mb-6 text-gray-900 flex items-center">
              <Search className="h-6 w-6 text-red-600 mr-2" />
              Find Your Ticket
            </h2>
            <form onSubmit={handleSearch} className="space-y-5">
              <div>
                <label htmlFor="ticket_id" className="block text-sm font-medium text-gray-700 mb-1">
                  Ticket ID *
                </label>
                <input
                  type="text"
                  id="ticket_id"
                  name="ticket_id"
                  value={searchData.ticket_id}
                  onChange={handleChange}
                  required
                  placeholder="e.g., IND-20260801-000001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent tracking-wider"
                />
              </div>

              <div>
                <label htmlFor="email_or_phone" className="block text-sm font-medium text-gray-700 mb-1">
                  Email or Phone Number *
                </label>
                <input
                  type="text"
                  id="email_or_phone"
                  name="email_or_phone"
                  value={searchData.email_or_phone}
                  onChange={handleChange}
                  required
                  placeholder="Enter the email or phone used while raising the request"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              {error && (
                <div className="p-4 rounded-md bg-red-50 text-red-800 border-l-4 border-red-500">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSearching}
                className="w-full bg-red-600 text-white px-6 py-3 rounded-md font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSearching ? (
                  'Searching...'
                ) : (
                  <>
                    <Search className="h-5 w-5 mr-2" />
                    Track Request
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Ticket Details */}
          {ticket && (
            <div className="mt-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              {/* Ticket Header */}
              <div className="bg-gradient-to-r from-gray-900 to-gray-700 text-white p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-gray-300 text-sm">Ticket ID</p>
                    <p className="text-2xl md:text-3xl font-bold tracking-wider">{ticket.ticket_id}</p>
                  </div>
                  <span
                    className={`inline-block px-4 py-2 rounded-full text-sm font-semibold border ${
                      statusStyles[ticket.status] || statusStyles.Open
                    }`}
                  >
                    {ticket.status}
                  </span>
                </div>
              </div>

              {/* Ticket Body */}
              <div className="p-6 md:p-8 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-3">
                    <User className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Customer Name</p>
                      <p className="text-gray-900 font-medium">{ticket.name}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Building2 className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Company</p>
                      <p className="text-gray-900 font-medium">{ticket.company || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Priority</p>
                      <p className={priorityStyles[ticket.priority] || 'text-gray-900'}>{ticket.priority}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <User className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Assigned To</p>
                      <p className="text-gray-900 font-medium">{ticket.assigned_to || 'Pending Assignment'}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Created On</p>
                      <p className="text-gray-900 font-medium">{formatDate(ticket.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Clock className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Last Updated</p>
                      <p className="text-gray-900 font-medium">{formatDate(ticket.updated_at)}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-start space-x-3">
                    <FileText className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 uppercase mb-1">Issue Description</p>
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">{ticket.issue_description}</p>
                    </div>
                  </div>
                </div>

                {ticket.resolution_notes && (
                  <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                    <div className="flex items-start space-x-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-green-700 uppercase font-semibold mb-1">Resolution Notes</p>
                        <p className="text-gray-700 whitespace-pre-wrap">{ticket.resolution_notes}</p>
                      </div>
                    </div>
                  </div>
                )}

                {ticket.closed_at && (
                  <div className="text-sm text-gray-500 border-t border-gray-200 pt-4">
                    Closed on: <span className="font-medium text-gray-700">{formatDate(ticket.closed_at)}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default TrackRequest;
